'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, useInView } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const Footer = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('2026');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear().toString());
  }, []);

  const footerSections: FooterSection[] = [
    {
      title: 'Quick Links',
      links: [
        { label: 'About Us', href: '/about' },
        { label: 'Our Services', href: '/services' },
        { label: 'Events', href: '/events' },
        { label: 'Gallery', href: '/gallery' },
      ],
    },
    {
      title: 'Get Involved',
      links: [
        { label: 'Donate', href: '/get-involved#donate' },
        { label: 'Volunteer', href: '/get-involved#volunteer' },
        { label: 'Partner With Us', href: '/get-involved#partnership' },
        { label: 'Corporate CSR', href: '/get-involved#csr' },
      ],
    },
    {
      title: 'Our Locations',
      links: [
        { label: 'Sarthana Center', href: '/locations#sarthana' },
        { label: 'Varachha Center', href: '/locations#varachha' },
        { label: 'Kadodara Center', href: '/locations#kadodara' },
        { label: 'All Locations', href: '/locations' },
      ],
    },
  ];

  const socialLinks = [
    { name: 'Facebook', icon: 'ShareIcon', href: '#' },
    { name: 'Instagram', icon: 'PhotoIcon', href: '#' },
    { name: 'Twitter', icon: 'ChatBubbleLeftIcon', href: '#' },
    { name: 'LinkedIn', icon: 'BriefcaseIcon', href: '#' },
  ];

  return (
    <footer ref={ref} className="bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 border-t border-border">
      <div className="container mx-auto px-4 py-10 md:py-16">
        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1,
                delayChildren: 0.1,
              },
            },
          }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-12 mb-8 md:mb-12"
        >
          {/* Brand Section */}
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 30 },
              visible: { opacity: 1, y: 0 },
            }}
            className="lg:col-span-2 space-y-6"
          >
            <Link href="/" className="flex items-center space-x-3 group">
              <div className="relative">
                <Image
                  src="/assets/images/logo.png"
                  alt="seva setu logo"
                  width={40}
                  height={40}
                />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-heading font-bold text-primary">
                  Seva Setu Trust
                </span>
                <span className="text-xs text-muted-foreground font-display">
                  सेवा ए परमो धर्म
                </span>
              </div>
            </Link>
            <p className="text-muted-foreground leading-relaxed">
              Bridging compassionate hearts with those in need through affordable healthcare,
              community support, and dignified service across Surat, Gujarat.
            </p>
            <div className="flex items-center space-x-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -3 }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="w-10 h-10 rounded-full bg-card hover:bg-primary hover:text-primary-foreground transition-all duration-300 flex items-center justify-center shadow-subtle hover:shadow-cta"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Footer Links */}
          {footerSections.map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0 },
              }}
              className="space-y-4"
            >
              <h3 className="text-lg font-heading font-bold text-foreground">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <motion.li
                    key={link.label}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link
                      href={link.href}
                      className="text-muted-foreground hover:text-primary transition-colors duration-300 flex items-center space-x-2 group"
                    >
                      <Icon
                        name="ChevronRightIcon"
                        size={16}
                        className="opacity-0 group-hover:opacity-100 -ml-4 group-hover:ml-0 transition-all duration-300"
                      />
                      <span>{link.label}</span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Newsletter Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-card rounded-2xl p-5 sm:p-8 mb-8 md:mb-12 shadow-subtle"
        >
          <div className="max-w-2xl mx-auto text-center space-y-4">
            <h3 className="text-2xl font-heading font-bold text-foreground">
              Stay Connected With Our Mission
            </h3>
            <p className="text-muted-foreground">
              Subscribe to receive updates about our events, impact stories, and ways to get involved.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="px-6 py-3 rounded-xl font-heading font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-cta hover:shadow-elevated whitespace-nowrap"
              >
                Subscribe
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="border-t border-border pt-8"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              {isHydrated ? `© ${currentYear}` : '© 2026'} Seva Setu Trust. All rights reserved. |
              Registered Charitable Trust
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
              <motion.div whileHover={{ scale: 1.05 }}>
                <Link href="#" className="hover:text-primary transition-colors duration-300">
                  Privacy Policy
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }}>
                <Link href="#" className="hover:text-primary transition-colors duration-300">
                  Terms of Service
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }}>
                <Link href="#" className="hover:text-primary transition-colors duration-300">
                  Refund Policy
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;